export type OfferPhase = 'active' | 'last_call' | 'ended';

export type OfferTrigger =
  | 'low_credits'
  | 'documents_created'
  | 'documents_sent'
  | 'returned_inactive'
  | 'first_signature'
  // Unlocks straight into Talk to founder: no discount window.
  | 'inactive_founder';

// A workspace's offer as the apps see it. The Dodo code stays on the server: it's applied at checkout.
export interface ExclusiveOffer {
  discountPercent: number;
  endsAt: string;
  lastCallHours: number;
  creditsReward: number;
  popupSeen: boolean;
}

export interface OfferTriggerThresholds {
  creditsBelow: number;
  documentsCreated: number;
  documentsSent: number;
  inactiveDays: number;
  inactiveDocuments: number;
  founderInactiveDays: number;
}

export interface OfferTriggerStats {
  availableCredits: number | null;
  documentsCreated: number;
  documentsSent: number;
  hasSignature: boolean;
  // How long the workspace's latest returning member had been away. Null when unknown.
  lastReturnInactiveSeconds: number | null;
}

// Asks whichever help launcher is on screen to open its book-a-call modal.
export const OPEN_BOOK_CALL_EVENT = 'proposal-biz:open-book-call';

const HOUR_MS = 60 * 60 * 1000;
const DAY_SECONDS = 24 * 60 * 60;

export function getOfferPhase(
  offer: Pick<ExclusiveOffer, 'endsAt' | 'lastCallHours'>,
  now = Date.now(),
): OfferPhase {
  const remaining = new Date(offer.endsAt).getTime() - now;
  if (remaining <= 0) return 'ended';
  return remaining <= offer.lastCallHours * HOUR_MS ? 'last_call' : 'active';
}

export function isFounderOnlyTriggerRun(
  inactiveSeconds: number | null,
  thresholds: Pick<OfferTriggerThresholds, 'founderInactiveDays'>,
): boolean {
  return (
    inactiveSeconds !== null && inactiveSeconds >= thresholds.founderInactiveDays * DAY_SECONDS
  );
}

// `availableCredits` is null for unlimited plans, which never run low. A long absence wins over
// every discount trigger: that member goes straight to Talk to founder.
export function getOfferTrigger(
  stats: OfferTriggerStats,
  thresholds: OfferTriggerThresholds,
): OfferTrigger | null {
  if (isFounderOnlyTriggerRun(stats.lastReturnInactiveSeconds, thresholds)) {
    return 'inactive_founder';
  }
  if (stats.hasSignature) return 'first_signature';
  if (stats.documentsSent >= thresholds.documentsSent) return 'documents_sent';
  if (
    stats.lastReturnInactiveSeconds !== null &&
    stats.lastReturnInactiveSeconds >= thresholds.inactiveDays * DAY_SECONDS &&
    stats.documentsCreated >= thresholds.inactiveDocuments
  ) {
    return 'returned_inactive';
  }
  if (stats.documentsCreated >= thresholds.documentsCreated) return 'documents_created';
  if (stats.availableCredits !== null && stats.availableCredits < thresholds.creditsBelow) {
    return 'low_credits';
  }
  return null;
}
